import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  Checkbox,
  InlineLoading,
  Module,
  ModuleBody,
  Wrapper,
} from "@wfp/ui";

import moment from "moment";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { Calendar, DateObject } from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";

import { Filters } from "types";

import styles from "../SchoolSelect/styles.module.scss";
import "../SchoolSelect/_content.scss";
import "react-multi-date-picker/styles/layouts/prime.css";

import {
  downloadDatasets,
  getDatasetsIsFetching,
} from "data-handler/ducks/datasets";
import {
  requestMVRefreshLog,
  getMVRefreshLog,
  getIsMVRefreshLogFetching,
} from "data-handler/ducks/mvRefreshLog";
import { getCurrentUser } from "data-handler/ducks/auth";
import { getSelectedCountry } from "data-handler/ducks/countryProfile";
import { getIsCountryAdmin } from "helpers/users";

const REAL_TIME_DATASETS = [
  "deliveries",
  "purchases",
  "consumptions",
  "incidents",
  "daily_entries",
  "approved_reports",
];

const generateAllDates = () => {
  const months = [];
  const dateEnd = moment();
  const dateStart = moment().subtract({ years: 3 });

  while (dateEnd.diff(dateStart, "months") >= 0) {
    const date = new DateObject();
    date.set({
      month: Number(dateStart.format("MM")),
      year: Number(dateStart.format("YYYY")),
    });
    months.push(date);
    dateStart.add(1, "month");
  }

  return months;
};

const RawDataExtractPage: React.ElementType = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const isFetching = useSelector(getDatasetsIsFetching);
  const mvRefreshLog = useSelector(getMVRefreshLog);
  const isMVRefreshLogFetching = useSelector(getIsMVRefreshLogFetching);
  const currentUser = useSelector(getCurrentUser);
  const selectedCountry = useSelector(getSelectedCountry);
  const selectedCountryForDownload =
    selectedCountry ||
    (getIsCountryAdmin(currentUser)
      ? currentUser?.country
      : currentUser?.countries?.length === 1
      ? currentUser.countries[0]
      : currentUser?.country);

  const [dateValue, setDateValue] = useState([] as DateObject[]);

  const methods = useForm({
    defaultValues: {},
  });

  const { control, handleSubmit, errors, setValue } = methods;
  const [countrySelectionError, setCountrySelectionError] = useState(false);

  useEffect(() => {
    dispatch(requestMVRefreshLog());
  }, [dispatch]);

  const getLastUpdatedForDataset = useMemo(() => {
    return (datasetValue: string): string | null => {
      const mvKey = `mv_${datasetValue}`;
      const results = (mvRefreshLog as any)?.results;
      if (!Array.isArray(results)) return null;

      for (const entry of results) {
        if (!entry || typeof entry !== "object") continue;
        const mvName: string | undefined =
          (entry as any).name ?? (entry as any).mv_name ?? (entry as any).key;
        if (mvName === mvKey) {
          const raw = (entry as any).refreshed_at || null;
          if (!raw) return null;
          const ts = moment.utc(raw).local();
          return ts.isValid()
            ? ts.format("YYYY-MM-DD HH:mm:ss ([GMT]Z)")
            : String(raw);
        }
      }
      return null;
    };
  }, [mvRefreshLog]);

  const isRealtimeDataset = (datasetValue: string) =>
    REAL_TIME_DATASETS.includes(datasetValue as any);

  const dateErrorMessage = (
    <label className="wfp--form-required" htmlFor="months">
      <FormattedMessage
        id="rawDataExtraction.errorMonthLabel"
        defaultMessage="* Please select at least 1 month"
      />
    </label>
  );

  const dateTitle = (
    <label className="wfp--label" htmlFor="months">
      <FormattedMessage
        id="rawDataExtraction.selectMonthsLabel"
        defaultMessage="Months"
      />
    </label>
  );

  const dateHelperText = (
    <label className="wfp--form__helper-text" htmlFor="months">
      <FormattedMessage
        id="rawDataExtraction.selectMonthsHelperLabel"
        defaultMessage="Select all months for which you are downloading"
      />
    </label>
  );

  const dataTypeErrorMessage = (
    <label className="wfp--form-required" htmlFor="dataType">
      <FormattedMessage
        id="rawDataExtraction.errorDataTypeLabel"
        defaultMessage="* Please select at least 1 category"
      />
    </label>
  );

  const dataTypeTitle = (
    <label className="wfp--label" htmlFor="raw data select">
      <FormattedMessage
        id="rawDataExtraction.selectDataTypeLabel"
        defaultMessage="Data Categories"
      />
    </label>
  );

  const countryErrorMessage = (
    <label className="wfp--form-required" htmlFor="selected-country-error">
      <FormattedMessage
        id="rawDataExtraction.errorCountryLabel"
        defaultMessage="* Please select a country from the Analytics header"
      />
    </label>
  );

  const allLabel = (
    <FormattedMessage
      id="Common.all"
      defaultMessage="All months from today to {date}"
      values={{ date: moment().subtract({ years: 3 }).format("MM/YYYY") }}
    />
  );

  const dataTypeHelperText = (
    <label className="wfp--form__helper-text" htmlFor="months">
      <FormattedMessage
        id="rawDataExtraction.selectDataTypeHelperLabel"
        defaultMessage="Select all categories for which you are downloading"
      />
    </label>
  );

  const datePanelHeaderLabel = (
    <FormattedMessage
      id="rawDataExtraction.datePanelHeaderLabel"
      defaultMessage="Selected Months"
    />
  );

  const rawDataOptions = useMemo(
    () => [
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeEnrolmentAttendance"
            defaultMessage="Enrolment and Attendance Monthly"
          />
        ),
        value: "enrolment_attendance_monthly",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeDeliveries"
            defaultMessage="Deliveries"
          />
        ),
        value: "deliveries",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypePurchases"
            defaultMessage="Purchases"
          />
        ),
        value: "purchases",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeConsumptions"
            defaultMessage="Consumptions"
          />
        ),
        value: "consumptions",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeIncidents"
            defaultMessage="Incidents"
          />
        ),
        value: "incidents",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeStockMovement"
            defaultMessage="Stock Movements Monthly"
          />
        ),
        value: "stock_movement_monthly",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeStockMovementWeekly"
            defaultMessage="Stock Movements Weekly"
          />
        ),
        value: "stock_movement_weekly",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeDailyEntries"
            defaultMessage="Daily Entries"
          />
        ),
        value: "daily_entries",
      },
      {
        label: (
          <FormattedMessage
            id="rawDataExtractionDataTypeApprovedReports"
            defaultMessage="Approved Reports"
          />
        ),
        value: "approved_reports",
      },
    ],
    []
  );

  // Parse URL query params for preselected dataset and months
  const preselectedDataset = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("dataset");
  }, [location.search]);

  const preselectedMonths = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const monthsParam = params.get("months");
    if (!monthsParam) return [];
    return monthsParam
      .split(",")
      .map((m) => m.trim())
      .filter(Boolean);
  }, [location.search]);

  // Parse additional dashboard filters passed via URL (adm, school, etc.)
  const extraFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const filterKeys = [
      "adm_1",
      "adm_2",
      "adm_3",
      "adm_4",
      "school_id",
      "school_year",
      "ongoing_school_year",
    ];
    const result: { id: string; value: string }[] = [];
    for (const key of filterKeys) {
      const values = params.getAll(key).filter(Boolean);
      if (values.length) {
        result.push({ id: key, value: values.join(",") });
      }
    }
    return result;
  }, [location.search]);

  const defaultDatasets = useMemo(() => {
    if (preselectedDataset) {
      const validValues = rawDataOptions.map((o) => String(o.value));
      const requested = preselectedDataset
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
      const valid = requested.filter((d) => validValues.includes(d));
      if (valid.length) {
        return valid;
      }
    }
    return [String(rawDataOptions[0].value)];
  }, [preselectedDataset, rawDataOptions]);

  const defaultDateValue = useMemo(() => {
    if (!preselectedMonths.length) return "";
    return preselectedMonths.join(",");
  }, [preselectedMonths]);

  const defaultDateObjects = useMemo(() => {
    if (!preselectedMonths.length) return [];
    return preselectedMonths
      .filter((m) => moment(m, "YYYY-MM", true).isValid())
      .map((m) => {
        const date = new DateObject();
        date.set({
          month: Number(moment(m, "YYYY-MM").format("MM")),
          year: Number(moment(m, "YYYY-MM").format("YYYY")),
        });
        return date;
      });
  }, [preselectedMonths]);

  // Apply preselected values to the form once
  useEffect(() => {
    if (defaultDateValue) {
      setValue("date", defaultDateValue);
      setDateValue(defaultDateObjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDateValue, defaultDateObjects]);

  const onSubmit = (values: any) => {
    if (!selectedCountryForDownload?.id) {
      setCountrySelectionError(true);
      return;
    }

    setCountrySelectionError(false);

    const countryFilter = {
      id: "country_id",
      value: String(selectedCountryForDownload.id),
    };

    const dates =
      values.date === "all"
        ? generateAllDates().map((date) => date.format("YYYY-MM"))
        : values.date.split(",");

    const dateFilter = dates.map((date: string) => ({
      id: "months",
      value: date,
    }));

    const filters: Filters = [countryFilter, ...dateFilter, ...extraFilters];

    const selectedDatasets: string[] = values.types || [];
    dispatch(downloadDatasets(selectedDatasets, filters));
  };

  return (
    <div className="raw-data-extract-page">
      <Wrapper
        pageWidth="sm"
        spacing="md"
        background="lighter"
        className="table"
      >
        <Module>
          <ModuleBody>
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="wfp--form wfp--form-long data-download-page"
              >
                <div className={styles.column}>
                  <div>
                    <div>
                      {dateTitle}
                      {dateHelperText}
                    </div>
                    <Controller
                      control={control}
                      name="date"
                      render={({ onChange, name }: any) => {
                        return (
                          <Calendar
                            value={dateValue}
                            minDate={moment()
                              .subtract(3, "years")
                              .startOf("month")
                              .toDate()}
                            className={
                              errors.hasOwnProperty("date")
                                ? "rmdp-prime rmdp-prime-error"
                                : "rmdp-prime"
                            }
                            multiple
                            onlyMonthPicker
                            onChange={(array: any) => {
                              setDateValue(array);
                              onChange(
                                array
                                  .map((date: any) => date.format("YYYY-MM"))
                                  .join(",")
                              );
                            }}
                            /*
                            // @ts-ignore */
                            name={name}
                            shadow={false}
                            styles={{ width: "auto" }}
                            datePanel={{ right: true }}
                            plugins={[
                              <DatePanel
                                sort="date"
                                /*
                                // @ts-ignore */
                                header={datePanelHeaderLabel}
                              />,
                            ]}
                          >
                            <Button
                              type="button"
                              onClick={() => {
                                setValue("date", "all");
                                setDateValue(generateAllDates());
                              }}
                            >
                              {allLabel}
                            </Button>
                          </Calendar>
                        );
                      }}
                      rules={{ required: true }}
                    />
                    <div className={styles.errorLabel}>
                      {errors.hasOwnProperty("date") && dateErrorMessage}
                    </div>
                  </div>

                  <div className="dataset-checkboxes">
                    <div>
                      {dataTypeTitle}
                      {dataTypeHelperText}
                    </div>
                    <Controller
                      control={control}
                      name="types"
                      render={({ onChange, value }) => {
                        const selected: string[] = value || [];
                        const toggle = (datasetValue: string) => {
                          if (selected.includes(datasetValue)) {
                            onChange(
                              selected.filter((v) => v !== datasetValue)
                            );
                          } else {
                            onChange([...selected, datasetValue]);
                          }
                        };
                        return (
                          <div className="dataset-checkbox-grid">
                            {rawDataOptions.map((option) => {
                              const datasetValue = String(option.value);
                              const checked = selected.includes(datasetValue);
                              const realtime = isRealtimeDataset(datasetValue);
                              const lastUpdated = getLastUpdatedForDataset(
                                datasetValue
                              );
                              return (
                                <div
                                  key={datasetValue}
                                  className={`dataset-checkbox-item${
                                    checked
                                      ? " dataset-checkbox-item--checked"
                                      : ""
                                  }`}
                                  onClick={() => toggle(datasetValue)}
                                  role="checkbox"
                                  aria-checked={checked}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === " " || e.key === "Enter") {
                                      e.preventDefault();
                                      toggle(datasetValue);
                                    }
                                  }}
                                >
                                  <Checkbox
                                    id={`dataset-${datasetValue}`}
                                    labelText={option.label}
                                    checked={checked}
                                    onChange={() => toggle(datasetValue)}
                                  />
                                  <div className="dataset-checkbox-info">
                                    {!isMVRefreshLogFetching &&
                                      (realtime ? (
                                        <FormattedMessage
                                          id="rawDataExtraction.realTimeData"
                                          defaultMessage="Real-time data"
                                        />
                                      ) : (
                                        <FormattedMessage
                                          id="rawDataExtraction.lastUpdated"
                                          defaultMessage="Last updated: {datetime}"
                                          values={{
                                            datetime: lastUpdated || "-",
                                          }}
                                        />
                                      ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                      defaultValue={defaultDatasets}
                      rules={{
                        validate: (v: string[]) =>
                          v && v.length > 0 ? true : false,
                      }}
                    />
                    <div className={styles.errorLabel}>
                      {errors.hasOwnProperty("types") && dataTypeErrorMessage}
                    </div>
                  </div>
                  <div className={styles.errorLabel}>
                    {countrySelectionError && countryErrorMessage}
                  </div>
                </div>

                <div className="footer-buttons">
                  <Button
                    type="button"
                    className="wfp--btn--secondary"
                    onClick={() => history.goBack()}
                  >
                    <FormattedMessage
                      id="ModalExtended.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                  <Button type="submit" disabled={isFetching}>
                    {isFetching ? (
                      <InlineLoading />
                    ) : (
                      <FormattedMessage
                        id="rawDataExtraction.download"
                        defaultMessage="Download"
                      />
                    )}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </ModuleBody>
        </Module>
      </Wrapper>
    </div>
  );
};

export default RawDataExtractPage;
